import { Injectable } from '@angular/core';
import {
  InventoryApplicationResourceProperties,
  InventoryAutomationResourceProperties,
  InventoryBotResourceProperties,
  InventoryConnectionResourceProperties,
  InventoryConnectorResourceProperties,
  InventoryConnectorResourceProvider,
  InventoryDataStorageResourceProperties,
  InventoryEntityField,
  InventoryEntityFieldStatus,
  InventoryEnvironmentResourceProperties,
} from 'src/app/models/api/inventoryResource.model';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
@Injectable({
  providedIn: 'root',
})
export class InventoryResourceCardFieldBuilder {
  private readonly _notAvailableString: string;

  constructor(
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe
  ) {
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
  }

  public pushAutomationStatusCardFieldIfSupported(
    extraProperties: InventoryAutomationResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.automationStatus,
      this._beautifyDisplayStringPipe.transform,
      'infoCardAutomationStatusFieldKey',
      'automation.automationStatus',
      cardFields
    );
  }

  public pushAutomationIsSolutionAwareCardFieldIfSupported(
    extraProperties: InventoryAutomationResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushBooleanCardFieldIfSupported(
      extraProperties.isSolutionAware,
      'infoCardAutomationIsSolutionAwareFieldKey',
      'automation.isSolutionAware',
      cardFields
    );
  }

  public pushAutomationIsBotTriggerCardFieldIfSuported(
    extraProperties: InventoryAutomationResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushBooleanCardFieldIfSupported(
      extraProperties.isBotTrigger,
      'infoCardAutomationIsBotTriggerFieldKey',
      'automation.isBotTrigger',
      cardFields
    );
  }

  public pushAutomationLastRunStatusCardFieldIfSupported(
    extraProperties: InventoryAutomationResourceProperties,
    cardFields: ICardField[]
  ) {
    if (extraProperties.executionInfo?.status === InventoryEntityFieldStatus.hasValue) {
      this.pushCardFieldIfSupported(
        extraProperties.executionInfo.value?.lastExecutionStatus as InventoryEntityField<string>,
        this._beautifyDisplayStringPipe.transform,
        'infoCardAutomationLastRunStatusFieldKey',
        'automation.automationLastRunStatus',
        cardFields
      );
    }
  }

  public pushConnectionInUseCardFieldIfSupported(
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushBooleanCardFieldIfSupported(
      extraProperties.inUse,
      'infoCardConnectionInUseFieldKey',
      'connection.inUse',
      cardFields
    );
  }

  public pushConnectionStatusCardFieldIfSupported(
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.connectionStatus,
      this._beautifyDisplayStringPipe.transform,
      'infoCardConnectionStatusFieldKey',
      'connection.connectionStatus',
      cardFields
    );
  }

  public pushConnectionHostNameCardFieldIfSupported(
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    if (extraProperties.hostName?.status === InventoryEntityFieldStatus.hasValue) {
      this.pushCardFieldIfSupported(
        extraProperties.hostName,
        (h) => h,
        'infoCardConnectionHostNameFieldKey',
        'connection.connectionHostName',
        cardFields
      );
    }
  }

  public pushConnectionsIsOnPremisesCardFieldIfSupported(
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushBooleanCardFieldIfSupported(
      extraProperties.isOnPremises,
      'infoCardConnectionIsOnPremisesFieldKey',
      'connection.isOnPremises',
      cardFields
    );
  }

  public pushConnectionAuthenticationTypeCardFieldIfSupported(
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfHasValue(
      extraProperties.authenticationMethod,
      (h) => h,
      'infoCardConnectionAuthenticationMethodFieldKey',
      'connection.authenticationMethod',
      cardFields
    );
  }

  public pushConnectionConnectorLinkCardField(
    integrationId: string,
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    const cardField = this.buildSupportedCardField(
      extraProperties.connector,
      (c) => c.displayName,
      'infoCardConnectionConnectorLinkFieldKey',
      'connection.connectorLink'
    );

    if (extraProperties.connector?.status === InventoryEntityFieldStatus.hasValue) {
      cardField.internalNavigation = {
        page: '',
        // We set violation to undefined so it's removed from the query string of the current page
        query: {
          integration: integrationId,
          resource: extraProperties.connector.value?.id,
          violation: undefined,
        },
      };
    }

    cardFields.push(cardField);
  }

  public pushConnectorProviderCardFieldsIfSupported(
    hasConnectorProviderField: {
      provider: InventoryEntityField<InventoryConnectorResourceProvider>;
    },
    cardFields: ICardField[]
  ) {
    if (this.isFieldSupported(hasConnectorProviderField.provider)) {
      cardFields.push(
        this.buildSupportedCardField(
          hasConnectorProviderField.provider,
          (v) => v.name,
          'infoCardConnectorProviderNameFieldKey',
          'connector.providerName'
        ),
        this.buildSupportedCardField(
          hasConnectorProviderField.provider,
          (v) => this._displayCamelCaseStringPipe.transform(v.category),
          'infoCardConnectorProviderCategoryFieldKey',
          'connector.providerCategory'
        )
      );
    }
  }

  public pushConnectorVersionFieldIfSupported(
    extraProperties: InventoryConnectorResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.version,
      (v) => v,
      'infoCardConnectorVersionFieldKey',
      'connector.version',
      cardFields
    );
  }

  public pushConnectorBackendServiceUrlFieldIfSupported(
    extraProperties: InventoryConnectorResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.backendServiceUrl,
      (v) => v,
      'infoCardConnectorBackendServiceUrlFieldKey',
      'connector.backendServiceUrl',
      cardFields
    );
  }

  public pushConnectorIsDeprecatedFieldIfSupported(
    extraProperties: InventoryConnectorResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushBooleanCardFieldIfSupported(
      extraProperties.isDeprecated,
      'infoCardConnectorIsDeprecatedFieldKey',
      'connector.isDeprecated',
      cardFields
    );
  }

  public pushEnvironmentTypeCardFieldIfSupported(
    extraProperties: InventoryEnvironmentResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.environmentType,
      this._displayCamelCaseStringPipe.transform,
      'infoCardEnvironmentTypeFieldKey',
      'environment.environmentType',
      cardFields
    );
  }

  public pushApplicationQuarantineStatusCardFieldIfSupported(
    extraProperties: InventoryApplicationResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.quarantineStatus,
      this._displayCamelCaseStringPipe.transform,
      'infoCardApplicationQuarantineStatusFieldKey',
      'application.quarantineStatus',
      cardFields
    );
  }

  public pushDataStorageTypeCardFieldIfSupported(
    extraProperties: InventoryDataStorageResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.entityName,
      (h) => h,
      'infoCardDataStorageEntityNameFieldKey',
      'dataStorage.entityName',
      cardFields
    );
    this.pushCardFieldIfSupported(
      extraProperties.dataStorageType,
      this._displayCamelCaseStringPipe.transform,
      'infoCardDataStorageTypeFieldKey',
      'dataStorage.dataStorageType',
      cardFields
    );
  }

  public pushBotTypeCardFieldIfSupported(extraProperties: InventoryBotResourceProperties, cardFields: ICardField[]) {
    this.pushCardFieldIfSupported(
      extraProperties.botType,
      this._displayCamelCaseStringPipe.transform,
      'infoCardBotTypeFieldKey',
      'bot.botType',
      cardFields
    );
  }

  public pushBotAutonomousTriggersCountCardFieldIfSupported(
    extraProperties: InventoryBotResourceProperties,
    cardFields: ICardField[]
  ) {
    this.pushCardFieldIfSupported(
      extraProperties.autonomousTriggersCount,
      (c) => c.toString(),
      'infoCardBotAutonomousTriggersCountFieldKey',
      'bot.autonomousTriggersCount',
      cardFields
    );
  }

  public pushBooleanCardFieldIfSupported(
    field: InventoryEntityField<boolean>,
    nameLabelInInventoryResourcePage: string,
    descriptionLabelPathUnderResourceSpecificAttributes: string,
    cardFields: ICardField[]
  ) {
    if (this.isFieldSupported(field)) {
      cardFields.push(
        this.buildSupportedCardField(
          field,
          (flag) => this._labelsProviderPipe.transform(flag ? 'common.yes' : 'common.no'),
          nameLabelInInventoryResourcePage,
          descriptionLabelPathUnderResourceSpecificAttributes
        )
      );
    }
  }

  public pushCardFieldIfSupported<TFieldValue>(
    field: InventoryEntityField<TFieldValue>,
    fieldDisplay: (value: TFieldValue) => string,
    nameLabelInInventoryResourcePage: string,
    descriptionLabelPathUnderResourceSpecificAttributes: string,
    cardFields: ICardField[],
    valueLink?: URL
  ) {
    if (this.isFieldSupported(field)) {
      cardFields.push(
        this.buildSupportedCardField(
          field,
          fieldDisplay,
          nameLabelInInventoryResourcePage,
          descriptionLabelPathUnderResourceSpecificAttributes,
          valueLink
        )
      );
    }
  }

  public pushCardFieldIfHasValue<TFieldValue>(
    field: InventoryEntityField<TFieldValue>,
    fieldDisplay: (value: TFieldValue) => string,
    nameLabelInInventoryResourcePage: string,
    descriptionLabelPathUnderResourceSpecificAttributes: string,
    cardFields: ICardField[]
  ) {
    if (field?.status === InventoryEntityFieldStatus.hasValue) {
      cardFields.push(
        this.buildSupportedCardField(
          field,
          fieldDisplay,
          nameLabelInInventoryResourcePage,
          descriptionLabelPathUnderResourceSpecificAttributes
        )
      );
    }
  }

  public buildSupportedCardField<TFieldValue>(
    field: InventoryEntityField<TFieldValue>,
    fieldDisplay: (value: TFieldValue) => string,
    nameLabelInInventoryResourcePage: string,
    descriptionLabelPathUnderResourceSpecificAttributes?: string,
    valueLink?: URL
  ): ICardField {
    return {
      key: this._labelsProviderPipe.transform('inventoryResourcePage.' + nameLabelInInventoryResourcePage),
      value:
        field?.status === InventoryEntityFieldStatus.hasValue ? fieldDisplay(field.value!) : this._notAvailableString,
      valueLink: valueLink?.toString(),
      descriptionLabelPath: descriptionLabelPathUnderResourceSpecificAttributes
        ? 'commonEntitiesInfo.resourceSpecificAttributes.' + descriptionLabelPathUnderResourceSpecificAttributes
        : undefined,
    };
  }

  public buildBasicCardFiled(
    generalNamedLabel: string,
    value?: string | null,
    descriptionLabelPathUnderResourceSpecificAttributes?: string,
    iconPath?: string,
    isCopyable?: boolean
  ): ICardField {
    return {
      key: this._labelsProviderPipe.transform(generalNamedLabel),
      value: value,
      descriptionLabelPath: descriptionLabelPathUnderResourceSpecificAttributes,
      iconPath: iconPath,
      isCopyable: isCopyable,
    };
  }

  public isFieldSupported(field: InventoryEntityField<unknown> | undefined): boolean {
    return !(
      field?.status === InventoryEntityFieldStatus.unsupportedForPlatformResourceType ||
      field?.status === InventoryEntityFieldStatus.irrelevantForResourceCategory ||
      field?.status === InventoryEntityFieldStatus.irrelevantForResourceType
    );
  }
}
